import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { devicesAPI, tenantsAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  FolderSimple,
  Plus,
  PencilSimple,
  Trash,
  Desktop,
  CircleNotch,
  MagnifyingGlass,
  Check,
  Buildings
} from '@phosphor-icons/react';

export default function GroupsPage() {
  const { isAdmin, isSuperadmin } = useAuth();
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterTenant, setFilterTenant] = useState('all');
  const [form, setForm] = useState({ name: '', description: '', device_ids: [], tenant_id: '' });
  const [deviceSearch, setDeviceSearch] = useState('');

  const fetchGroups = useCallback(async () => {
    try {
      const params = filterTenant !== 'all' ? `?tenant_id=${filterTenant}` : '';
      const res = await api.get(`/groups${params}`);
      setGroups(res.data.groups);
    } catch (e) {
      toast.error('Gruppen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [filterTenant]);

  const fetchDevices = async () => {
    try {
      const res = await devicesAPI.getAll({ per_page: 500 });
      setDevices(res.data.devices);
    } catch (e) {
      console.error('Failed to fetch devices:', e);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await tenantsAPI.getAll();
      setTenants(res.data);
    } catch (e) {
      console.error('Failed to fetch tenants:', e);
    }
  };

  useEffect(() => { fetchGroups(); }, [fetchGroups]);
  useEffect(() => { fetchDevices(); if (isSuperadmin) fetchTenants(); }, [isSuperadmin]);

  const openCreate = () => {
    setForm({ name: '', description: '', device_ids: [], tenant_id: tenants.length > 0 ? tenants[0].id : '' });
    setDeviceSearch('');
    setCreateOpen(true);
  };

  const openEdit = (group) => {
    setForm({ name: group.name, description: group.description || '', device_ids: group.device_ids || [], tenant_id: group.tenant_id || '' });
    setDeviceSearch('');
    setEditGroup(group);
  };

  const toggleDevice = (deviceId) => {
    setForm(prev => ({
      ...prev,
      device_ids: prev.device_ids.includes(deviceId)
        ? prev.device_ids.filter(id => id !== deviceId)
        : [...prev.device_ids, deviceId]
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name ist erforderlich'); return; }
    if (isSuperadmin && !form.tenant_id) { toast.error('Bitte Mandant auswählen'); return; }
    setSaving(true);
    try {
      await api.post('/groups', form);
      toast.success('Gruppe erstellt');
      setCreateOpen(false);
      fetchGroups();
    } catch (e) {
      toast.error('Fehler beim Erstellen');
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/groups/${editGroup.id}`, { name: form.name, description: form.description, device_ids: form.device_ids });
      toast.success('Gruppe aktualisiert');
      setEditGroup(null);
      fetchGroups();
    } catch (e) {
      toast.error('Fehler beim Aktualisieren');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/groups/${deleteGroup.id}`);
      toast.success('Gruppe gelöscht');
      setDeleteGroup(null);
      fetchGroups();
    } catch (e) {
      toast.error('Fehler beim Löschen');
    } finally { setSaving(false); }
  };

  // Filter devices by selected tenant in create dialog
  const devicesForTenant = form.tenant_id
    ? devices.filter(d => d.tenant_id === form.tenant_id)
    : devices;

  const filteredDevices = devicesForTenant.filter(d =>
    d.computer_name.toLowerCase().includes(deviceSearch.toLowerCase()) ||
    (d.location_city || '').toLowerCase().includes(deviceSearch.toLowerCase())
  );

  // Group groups by tenant for display
  const groupsByTenant = {};
  groups.forEach(g => {
    const key = g.tenant_name || 'Unbekannt';
    if (!groupsByTenant[key]) groupsByTenant[key] = [];
    groupsByTenant[key].push(g);
  });

  const DeviceSelector = () => (
    <div className="mt-4">
      <Label className="text-[#a1a1aa]">Geräte zuweisen ({form.device_ids.length} ausgewählt)</Label>
      <div className="relative mt-1 mb-2">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525b]" />
        <Input
          placeholder="Geräte suchen..."
          value={deviceSearch}
          onChange={e => setDeviceSearch(e.target.value)}
          className="pl-9 bg-[#0A0A0B] border-white/10 text-sm"
        />
      </div>
      <div className="max-h-48 overflow-y-auto bg-[#0A0A0B] border border-white/10 rounded">
        {filteredDevices.map(d => (
          <div
            key={d.id}
            onClick={() => toggleDevice(d.id)}
            className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
              form.device_ids.includes(d.id) ? 'bg-[#D50C2D]/10 text-white' : 'text-[#a1a1aa] hover:bg-white/5'
            }`}
          >
            <div className={`w-5 h-5 rounded border flex items-center justify-center ${
              form.device_ids.includes(d.id) ? 'bg-[#D50C2D] border-[#D50C2D]' : 'border-white/20'
            }`}>
              {form.device_ids.includes(d.id) && <Check size={12} className="text-white" />}
            </div>
            <Desktop size={14} className="text-[#52525b]" />
            <span className="text-sm">{d.computer_name}</span>
            {d.location_city && <span className="text-[#52525b] text-xs">({d.location_city})</span>}
            <span className={`ml-auto text-xs ${d.is_online ? 'text-green-400' : 'text-[#52525b]'}`}>
              {d.is_online ? 'Online' : 'Offline'}
            </span>
          </div>
        ))}
        {filteredDevices.length === 0 && (
          <p className="text-[#52525b] text-sm text-center py-4">
            {form.tenant_id ? 'Keine Geräte für diesen Mandanten' : 'Keine Geräte gefunden'}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="groups-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Geräte-Gruppen</h1>
          <p className="text-[#a1a1aa] text-sm mt-1">{groups.length} Gruppe{groups.length !== 1 ? 'n' : ''}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isSuperadmin && (
            <Select value={filterTenant} onValueChange={v => setFilterTenant(v)}>
              <SelectTrigger className="w-[160px] sm:w-[180px] bg-[#0A0A0B] border-white/10" data-testid="tenant-filter">
                <SelectValue placeholder="Alle Mandanten" />
              </SelectTrigger>
              <SelectContent className="bg-[#141416] border-white/10">
                <SelectItem value="all">Alle Mandanten</SelectItem>
                {tenants.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isAdmin && (
            <Button onClick={openCreate} className="bg-[#D50C2D] hover:bg-[#f21d41]" data-testid="create-group-btn">
              <Plus size={16} />
              <span>Neue Gruppe</span>
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <CircleNotch size={32} className="animate-spin text-[#D50C2D]" />
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 bg-[#141416] border border-white/10 rounded">
          <FolderSimple size={48} className="mx-auto text-[#52525b] mb-4" />
          <p className="text-[#a1a1aa]">Noch keine Gruppen erstellt</p>
          <p className="text-[#52525b] text-sm mt-1">Erstelle Gruppen, um Geräte zu organisieren (z.B. je Bundesland oder Stadt)</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupsByTenant).map(([tenantName, tenantGroups]) => (
            <div key={tenantName}>
              {isSuperadmin && (
                <div className="flex items-center gap-2 mb-3">
                  <Buildings size={16} className="text-[#D50C2D]" />
                  <h2 className="text-white font-heading font-semibold text-sm uppercase tracking-wider">{tenantName}</h2>
                  <span className="text-[#52525b] text-xs">({tenantGroups.length})</span>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tenantGroups.map(group => (
                  <div key={group.id} className="stat-card" data-testid={`group-card-${group.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D50C2D]/10 rounded flex items-center justify-center">
                          <FolderSimple size={20} className="text-[#D50C2D]" />
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-white">{group.name}</h3>
                          {group.description && <p className="text-[#52525b] text-xs mt-0.5">{group.description}</p>}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(group)} className="p-1.5 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded" data-testid={`edit-group-${group.id}`}>
                            <PencilSimple size={14} />
                          </button>
                          <button onClick={() => setDeleteGroup(group)} className="p-1.5 text-[#a1a1aa] hover:text-red-400 hover:bg-red-500/10 rounded" data-testid={`delete-group-${group.id}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Desktop size={14} className="text-[#52525b]" />
                        <span className="text-[#a1a1aa]">{group.device_count} Gerät{group.device_count !== 1 ? 'e' : ''}</span>
                      </div>
                      {group.tenant_code && (
                        <span className="tenant-badge">{group.tenant_code}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Neue Gruppe</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {isSuperadmin && (
              <div>
                <Label className="text-[#a1a1aa]">Mandant</Label>
                <Select value={form.tenant_id} onValueChange={v => setForm(p => ({...p, tenant_id: v, device_ids: []}))}>
                  <SelectTrigger className="bg-[#0A0A0B] border-white/10 mt-1" data-testid="group-tenant-select">
                    <SelectValue placeholder="Mandant auswählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141416] border-white/10">
                    {tenants.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[#a1a1aa]">Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" placeholder="z.B. Berlin, Bayern, NRW..." />
            </div>
            <div>
              <Label className="text-[#a1a1aa]">Beschreibung</Label>
              <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" placeholder="z.B. Alle Stationen in Berlin" />
            </div>
            <DeviceSelector />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-white/10">Abbrechen</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editGroup} onOpenChange={() => setEditGroup(null)}>
        <DialogContent className="bg-[#141416] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Gruppe bearbeiten</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[#a1a1aa]">Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-[#a1a1aa]">Beschreibung</Label>
              <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="bg-[#0A0A0B] border-white/10 mt-1" />
            </div>
            <DeviceSelector />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroup(null)} className="border-white/10">Abbrechen</Button>
            <Button onClick={handleEdit} disabled={saving} className="bg-[#D50C2D] hover:bg-[#f21d41]">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteGroup} onOpenChange={() => setDeleteGroup(null)}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading">Gruppe löschen</DialogTitle></DialogHeader>
          <p className="text-[#a1a1aa]">
            Bist du sicher, dass du <span className="text-white font-medium">{deleteGroup?.name}</span> löschen möchtest?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteGroup(null)} className="border-white/10">Abbrechen</Button>
            <Button onClick={handleDelete} disabled={saving} variant="destructive">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
